import { useEffect, useMemo, useState } from "react";
import { FormattedMessage as FM } from "react-intl";
import { signal } from "@preact/signals-react";

import proxy from "api/proxy";
import StdButton from "components/Buttons/StdButton";
import CharField from "components/MeForm/CharField";
import { Alert } from "componentsv2/widgets/Alert";
import AppearanceSectionTitle from "./AppearanceSectionTitle";
import ColorPickerField from "./ColorPickerField";
import SitePreview from "./SitePreview";
import SectionToggle from "./SectionToggle";
import {
  fetchHotelConfig,
  getHotelDefaults,
  resolveHotelData,
} from "../helpers/hotelDefaults";
import { getCreatedId } from "../helpers/listEditor";
import {
  MELHOUS_MODEL,
  COLOR_FIELDS,
  SECTION_FIELDS,
  APPEARANCE_FIELDS,
  SITE_FIELDS,
  STYLE_SITE_LAYOUT,
  STYLE_SITE_ASIDE,
  STYLE_ACTION_BUTTON,
} from "../constants";

const attrs = { level: "wizard" };

function getColors(source) {
  return Object.fromEntries(
    COLOR_FIELDS.map((name) => [name, source?.[name] ?? null]),
  );
}

function createSignals() {
  return Object.fromEntries(
    APPEARANCE_FIELDS.map((name) => [name, signal(null)]),
  );
}

function AppearanceForm() {
  const values = useMemo(createSignals, []);
  const [recordId, setRecordId] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [melhous, setMelhous] = useState(null);
  const [colors, setColors] = useState(getColors);
  const [defaults, setDefaults] = useState({});
  const [colorsReset, setColorsReset] = useState(false);
  const [sections, setSections] = useState({});

  async function getConfig() {
    const { data } = await proxy.search(
      MELHOUS_MODEL,
      [],
      [...new Set([...APPEARANCE_FIELDS, ...SITE_FIELDS])],
      1,
    );
    const current = data?.[0];
    if (!current) return;
    setMelhous(current);
    setColors(getColors(current));
    setRecordId(current.id);
    APPEARANCE_FIELDS.forEach((name) => {
      values[name].value = current[name] ?? null;
    });
    SECTION_FIELDS.forEach((name) => {
      values[name].value = current[name] !== false;
    });
    setSections(
      Object.fromEntries(
        SECTION_FIELDS.map((name) => [name, current[name] !== false]),
      ),
    );
  }

  function handleSectionChange(name, value) {
    values[name].value = value;
    setSections((prev) => ({ ...prev, [name]: value }));
  }

  async function loadDefaults() {
    const config = await fetchHotelConfig();
    setDefaults(getHotelDefaults(config));
  }

  function resetColors() {
    COLOR_FIELDS.forEach((name) => {
      values[name].value = null;
    });
    setColors(getColors());
    setColorsReset(true);
  }

  function handleColorChange(name, value) {
    setColors((prev) => ({ ...prev, [name]: value }));
  }

  async function saveConfig() {
    const storeRec = Object.fromEntries(
      APPEARANCE_FIELDS.map((name) => [name, values[name].value || null]),
    );
    SECTION_FIELDS.forEach((name) => {
      storeRec[name] = Boolean(values[name].value);
    });
    if (recordId) storeRec.id = recordId;
    const res = await proxy.saveQuery({
      model: MELHOUS_MODEL,
      storeRec,
      fieldsNames: ["id"],
    });
    if (!res || res.error) return;
    if (!recordId) setRecordId(getCreatedId(res.data));
    setColorsReset(false);
    setShowAlert(true);
  }

  function closeAlert() {
    setShowAlert(false);
  }

  useEffect(() => {
    getConfig();
    loadDefaults();
  }, []);

  return (
    <div className="w-full">
      <div className={STYLE_SITE_LAYOUT}>
        <div className="min-w-0 space-y-6 py-4">
          <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
            <AppearanceSectionTitle id="melhous.dash.colors" />
            {COLOR_FIELDS.map((name) => (
              <ColorPickerField
                key={name}
                field={{ name, label: `melhous.${name}`, type: "char" }}
                data={values[name]}
                value={colors[name]}
                onValueChange={handleColorChange}
                attrs={attrs}
                inherited={defaults[name]}
              />
            ))}
            <div className="col-span-full mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={resetColors}
                className={STYLE_ACTION_BUTTON}
              >
                <FM id="melhous.dash.color.reset" />
              </button>
              {colorsReset && (
                <span className="text-xs text-neutral-500">
                  <FM id="melhous.dash.color.reset_hint" />
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
            <AppearanceSectionTitle id="melhous.dash.sections" />
            <div className="col-span-full mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {SECTION_FIELDS.map((name) => (
                <SectionToggle
                  key={name}
                  name={name}
                  checked={sections[name] !== false}
                  onChange={handleSectionChange}
                />
              ))}
            </div>
            <CharField
              field={{
                name: "logo_link",
                label: "melhous.logo_link",
                placeholder: "https://",
              }}
              data={values.logo_link}
              attrs={attrs}
            />
          </div>
          <Alert
            text="melhous.dash.saved"
            show={showAlert}
            onClose={closeAlert}
            color="bg-green-200 text-green-800"
            icon="check"
            autoRemove={true}
          />
          <div className="pb-3 w-full">
            <StdButton
              onClick={saveConfig}
              color="black"
              size="w-full"
              style="mx-auto uppercase"
              content={recordId ? "melhous.dash.save" : "melhous.dash.create"}
            />
          </div>
        </div>
        <aside className={STYLE_SITE_ASIDE}>
          <SitePreview
            data={resolveHotelData({ ...melhous, ...colors }, defaults)}
          />
        </aside>
      </div>
    </div>
  );
}

export default AppearanceForm;
